// SuggestionCard — auto-generated optimization card.
//
// Given a RAW v2 suggestion, it resolves the evidence once, selects the block
// layout by `archetype`, and renders only the blocks whose evidence is present
// (each block self-guards). It reads v2 fields with legacy `evidence.*` fallback
// (see utils/suggestionEvidence) and does NOT branch on rule keys.
//
// Chrome added by SuggestionCard: a compact header (top) and a unified action
// footer (bottom) with `Descartar` + a primary CTA, consistent across every card.
// The CTA is derived from the data (utils/suggestionActions): `Aplicar` builds the
// archetype-correct /apply request; NO_ACTION alerts show `Investigar` (deep-link).
// Multi-item / sub-item cards make their rows selectable so the built request acts
// on the chosen subset. `onApply(suggestion, applyRequest)` /
// `onDismiss(suggestion, dismissRequest)` receive the built request and execute it
// (e.g. via services/optimizationActions); without them the buttons are inert.
//
// `SuggestionCardBody` renders just the blocks (no header/footer).
import React, { useCallback, useMemo, useState } from 'react';
import { getIntentTone } from './subcomponents/intentTone';
import StatusPill from './subcomponents/StatusPill';
import { resolveSuggestionEvidence, resolveCurrency, resolveVisibleItems, resolveSelectableItems } from 'utils/suggestionEvidence';
import { resolvePrimaryCta, buildApplyRequest, buildDismissRequest } from 'utils/suggestionActions';
import { sanitizeOptimizationDescription } from 'utils/sanitizeOptimizationDescription';
import {
  BLOCKS,
  ARCHETYPE_LAYOUT,
  DEFAULT_LAYOUT,
  CRITERIA_TITLE,
  DEFAULT_CRITERIA_TITLE,
} from './suggestionCardBlocks';

const STATUS_META = {
  NEW: { label: 'Nueva', tone: 'info' },
  SHOWN: { label: 'Vista', tone: 'neutral' },
  LIKED: { label: 'Marcada útil', tone: 'success' },
  APPLIED: { label: 'Aplicada', tone: 'success' },
  DISMISSED: { label: 'Descartada', tone: 'warning' },
  EXPIRED: { label: 'Expirada', tone: 'neutral' },
  FAILED: { label: 'Falló', tone: 'danger' },
  SUPERSEDED: { label: 'Reemplazada', tone: 'neutral' },
};

const metaLine = (s) => [
  s?.platform,
  s?.archetype ? `Arquetipo ${String(s.archetype).toUpperCase()}` : null,
  s?.intent,
].filter(Boolean).join(' · ');

const cleanId = (v) => (v != null ? String(v) : '');

// Selection is derived from the archetype's RENDERED layout, so the footer count
// only gates Aplicar when the body actually shows a picker:
//   • layout has `subItems` + suggestion has sub_items → choose sub-items (archetype F)
//   • layout has `multiItems` + ≥2 ads → choose items. Header layouts (A/B/E/H) list
//     every ad incl. the primary, which replaces the lone header (hideHeader); value-led
//     layouts (D, no entity header) keep their header and list the OTHER affected items.
//   • otherwise → no selection (whole-suggestion apply)
// Deep-link cards (applicable === false) and NO_ACTION alerts never select — their CTA
// is a "Ver en {Platform}" / "Investigar" link, not "Aplicar".
const resolveSelection = (s, layout) => {
  const subs = Array.isArray(s?.sub_items) ? s.sub_items : [];
  if (subs.length && layout.includes('subItems')) {
    const ids = subs.map((si) => cleanId(si?.id)).filter(Boolean);
    const defaultIds = subs
      .filter((si) => si?.selected_by_default !== false)
      .map((si) => cleanId(si?.id))
      .filter(Boolean);
    return { mode: 'subitems', items: null, hideHeader: false, ids, defaultIds };
  }
  if (layout.includes('multiItems')) {
    const usesHeader = layout.includes('entityHeader');
    const items = usesHeader ? resolveSelectableItems(s) : resolveVisibleItems(s);
    const ids = items.map((it) => cleanId(it?.id)).filter(Boolean);
    if (ids.length > 1) {
      // applicable === false (or NO_ACTION) → can't auto-apply: list every winner as a
      // per-row deep link (no checkboxes/count). Otherwise it's a selectable apply picker.
      const deepLink = s?.applicable === false || String(s?.action_type || '').toUpperCase() === 'NO_ACTION';
      return {
        mode: deepLink ? 'links' : 'items',
        items,
        hideHeader: usesHeader,
        ids,
        defaultIds: deepLink ? [] : ids,
      };
    }
  }
  return { mode: 'none', items: null, hideHeader: false, ids: [], defaultIds: [] };
};

export function SuggestionCardBody({
  suggestion,
  isItemsExpanded,
  setIsItemsExpanded,
  valueAfterRaw,
  setValueAfterRaw,
  selectedIds,
  toggleSelected,
  itemsPicker = false,
  itemsDeepLink = false,
  hideHeader = false,
  selectableItems = null,
}) {
  const s = suggestion || {};
  const intent = s?.intent ?? null;
  const archetype = String(s?.archetype || '').toUpperCase();
  const resolved = resolveSuggestionEvidence(s);
  const layout = ARCHETYPE_LAYOUT[archetype] || DEFAULT_LAYOUT;

  // The standalone criteria tiles (criteriaBars) duplicate the per-row criteria chips
  // when the card renders ≥2 ad rows above them (multiItems before criteriaBars, as in
  // archetype A "Iterar anuncio ganador"). Suppress them then — the rows already carry
  // the same criteria, per ad. See the criteriaBars block in suggestionCardBlocks.
  const itemRows = (itemsPicker || itemsDeepLink) ? (selectableItems || []) : (resolved.visibleItems || []);
  const miIdx = layout.indexOf('multiItems');
  const cbIdx = layout.indexOf('criteriaBars');
  const suppressRedundantCriteria =
    itemRows.length > 1 &&
    miIdx !== -1 && cbIdx !== -1 && miIdx < cbIdx &&
    Array.isArray(resolved.criteria) && resolved.criteria.length > 0;

  const ctx = {
    suggestion: s,
    resolved,
    intent,
    tone: getIntentTone(intent),
    currency: resolveCurrency(s),
    criteriaTitle: CRITERIA_TITLE[archetype] || DEFAULT_CRITERIA_TITLE,
    isItemsExpanded,
    setIsItemsExpanded,
    valueAfterRaw,
    setValueAfterRaw,
    selectedIds,
    toggleSelected,
    itemsPicker,
    itemsDeepLink,
    hideHeader,
    selectableItems,
    suppressRedundantCriteria,
  };
  return (
    <div className="space-y-4">
      {layout.map((blockKey) => {
        const block = BLOCKS[blockKey];
        const node = block ? block(ctx) : null;
        return node ? <div key={blockKey}>{node}</div> : null;
      })}
    </div>
  );
}

export default function SuggestionCard({ suggestion, className = '', onApply, onDismiss, onMarkSeen }) {
  const s = useMemo(() => suggestion || {}, [suggestion]);
  const archetype = String(s?.archetype || '').toUpperCase();
  const layout = ARCHETYPE_LAYOUT[archetype] || DEFAULT_LAYOUT;

  // What the footer can act on, derived from the rendered layout (see resolveSelection).
  const selection = useMemo(() => resolveSelection(s, layout), [s, layout]);
  const itemsPicker = selection.mode === 'items';
  const itemsDeepLink = selection.mode === 'links';
  const hideHeader = selection.hideHeader;
  // Only apply-pickers (items/subitems) gate the footer count; deep-link lists don't.
  const hasSelectable = (selection.mode === 'items' || selection.mode === 'subitems') && selection.ids.length > 0;
  const selectableItems = selection.items;

  // Picker / deep-link / multi-item cards open expanded so the rows are visible.
  const [isItemsExpanded, setIsItemsExpanded] = useState(
    () => itemsPicker || itemsDeepLink || resolveVisibleItems(s).length > 1
  );
  // Editable "after" value (archetype D, and value-modifying B/C), seeded from the
  // proposed value_after (value_change_display for D, the compat field otherwise).
  const [valueAfterRaw, setValueAfterRaw] = useState(() => {
    const after = s?.value_change_display?.value_after ?? s?.value_after;
    return after != null ? String(after) : '';
  });
  const [selectedIds, setSelectedIds] = useState(() => selection.defaultIds);

  const toggleSelected = useCallback((id) => {
    const k = String(id);
    setSelectedIds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));
  }, []);

  const statusMeta = STATUS_META[String(s?.status || '').toUpperCase()] || null;
  // The action label (e.g. "Pausar anuncio") renders as an outlined chip next to
  // the status pill — border in the intent colour, transparent background.
  const actionLabel = s?.action_label || null;
  const tone = getIntentTone(s?.intent);
  const title = s?.rule?.name || s?.action_label || 'Optimización';
  const applyDisabled = hasSelectable && selectedIds.length === 0;

  // CTA + handlers are derived from the suggestion data (utils/suggestionActions):
  // the card builds the exact /apply or /dismiss request and hands it to the host.
  const cta = useMemo(() => resolvePrimaryCta(suggestion || {}), [suggestion]);

  // The card's description (user_text, else rule/suggestion description) is shown
  // directly below the title — replacing the platform · archetype · intent meta line.
  const descriptionHtml = useMemo(() => {
    const raw = s?.user_text || s?.rule?.description || s?.description || '';
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    return trimmed ? sanitizeOptimizationDescription(trimmed, { fallbackText: '', pClassName: 'm-0' }) : '';
  }, [s]);

  const handleApply = useCallback(() => {
    if (cta.requiresReview) {
      const confirmed = typeof window !== 'undefined' && typeof window.confirm === 'function'
        ? window.confirm('Este cambio estructural requiere revisión humana. ¿Confirmás que lo revisaste?')
        : true;
      if (!confirmed) return;
    }
    // Only forward a selection when the card actually offers a picker; otherwise the
    // apply targets the whole suggestion (passing [] here would mean "no items").
    const request = buildApplyRequest(s, {
      valueAfter: valueAfterRaw,
      selectedIds: selection.mode === 'none' ? null : selectedIds,
      confirmReviewDone: cta.requiresReview,
    });
    if (request) onApply?.(s, request);
  }, [cta, s, valueAfterRaw, selection.mode, selectedIds, onApply]);

  const handleDismiss = useCallback(() => {
    onDismiss?.(s, buildDismissRequest(s));
  }, [s, onDismiss]);

  // Multi-item deep-link cards (e.g. "Iterar anuncio ganador") have no single entity to
  // open, so a card-level "Ver en {Platform}" is ambiguous. Instead, "Entendido" just
  // marks the suggestion as seen — falls back to onDismiss when the host wires no onMarkSeen.
  const handleMarkSeen = useCallback(() => {
    const request = buildDismissRequest(s);
    if (!request) return;
    (onMarkSeen || onDismiss)?.(s, request);
  }, [s, onMarkSeen, onDismiss]);

  const handleInvestigate = useCallback(() => {
    if (cta.url && typeof window !== 'undefined' && typeof window.open === 'function') {
      window.open(cta.url, '_blank', 'noopener');
    }
  }, [cta]);

  return (
    <article
      className={`backdrop-blur-xl bg-gradient-to-br from-slate-800/80 via-slate-900/80 to-indigo-900/80 border border-white/15 rounded-2xl p-5 shadow-lg ${className}`}
    >
      <div className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-white truncate" title={title}>{title}</h3>
            {!descriptionHtml ? (
              <div className="mt-0.5 text-xs text-white/55 truncate">{metaLine(s)}</div>
            ) : null}
          </div>
          {statusMeta || actionLabel ? (
            <div className="shrink-0 flex items-center gap-2">
              {statusMeta ? <StatusPill label={statusMeta.label} tone={statusMeta.tone} /> : null}
              {actionLabel ? (
                <span
                  className="inline-flex items-center rounded-full border bg-transparent px-3 py-1 text-xs font-medium whitespace-nowrap"
                  style={{ borderColor: tone.fill, color: tone.text }}
                >
                  {actionLabel}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        {descriptionHtml ? (
          <div
            className="mt-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80 leading-snug space-y-1"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        ) : null}
      </div>

      <SuggestionCardBody
        suggestion={s}
        isItemsExpanded={isItemsExpanded}
        setIsItemsExpanded={setIsItemsExpanded}
        valueAfterRaw={valueAfterRaw}
        setValueAfterRaw={setValueAfterRaw}
        selectedIds={selectedIds}
        toggleSelected={toggleSelected}
        itemsPicker={itemsPicker}
        itemsDeepLink={itemsDeepLink}
        hideHeader={hideHeader}
        selectableItems={selectableItems}
      />

      <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={handleDismiss}
          className="min-h-[40px] px-4 rounded-lg border border-white/15 bg-white/5 text-sm font-semibold text-white/80 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          Descartar
        </button>
        {itemsDeepLink ? (
          <button
            type="button"
            onClick={handleMarkSeen}
            className="min-h-[40px] px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50"
          >
            Entendido
          </button>
        ) : cta.kind === 'investigate' ? (
          <button
            type="button"
            disabled={!cta.url}
            onClick={handleInvestigate}
            className="min-h-[40px] px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50"
          >
            {cta.label}
          </button>
        ) : (
          <button
            type="button"
            disabled={applyDisabled}
            onClick={handleApply}
            className="min-h-[40px] px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/50"
          >
            {cta.label}{hasSelectable ? ` (${selectedIds.length})` : ''}
          </button>
        )}
      </div>
    </article>
  );
}
